/**
 * ReadinessTab — Agent Readiness board (Phase 2).
 * Batch-scoped via the shared Department ▸ Batch filter bar. Shows every
 * trainee's composite score, state, trend and coverage; drill-down drawer
 * explains the number input by input.
 */
import { useCallback, useEffect, useState } from 'react';
import { Download, Gauge, RefreshCw, Settings2 } from 'lucide-react';
import { toast } from 'sonner';

import api from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { TraineeFilterBar, useTraineeFilter, ALL } from './TraineeFilterContext';
import { ReadinessBoardTable, StateChip } from './ReadinessBoardTable';
import ReadinessDrawer from './ReadinessDrawer';
import ReadinessProfilesDialog from './ReadinessProfilesDialog';

const STATE_ORDER = ['floor_ready', 'on_track', 'at_risk', 'not_ready', 'not_scored'];

function daysRemaining(endDate) {
  if (!endDate) return null;
  const d = Math.ceil((new Date(endDate) - new Date()) / 86400000);
  return Number.isFinite(d) ? d : null;
}

export default function ReadinessTab() {
  const { get, post } = useApi();
  const { hasPermission } = useAuth();
  const { batchId } = useTraineeFilter();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);

  const load = useCallback(() => {
    if (batchId === ALL) { setBoard(null); return; }
    setLoading(true);
    get(`/readiness/batch/${batchId}`)
      .then(setBoard)
      .catch(() => setBoard(null))
      .finally(() => setLoading(false));
  }, [get, batchId]);

  useEffect(load, [load]);

  const recompute = async () => {
    setRecomputing(true);
    try {
      await post('/readiness/recompute', { batch_id: batchId });
      toast.success('Readiness scores recomputed');
      load();
    } catch (err) {
      if (err.response?.status === 429) {
        toast.info(err.response.data?.detail || 'Recently computed — scores are fresh.');
      } else {
        toast.error(err.response?.data?.detail || 'Recompute failed');
      }
    } finally { setRecomputing(false); }
  };

  const exportCalibration = async () => {
    try {
      const res = await api.get(`/readiness/batch/${batchId}/export.xlsx`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `readiness-calibration-${board?.batch?.name || batchId}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('Calibration sheet downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="readiness-tab">
      <TraineeFilterBar extra={
        <div className="flex items-center gap-2">
          {batchId !== ALL && board && (
            <Button variant="outline" size="sm" onClick={exportCalibration}
                    data-testid="readiness-export-btn">
              <Download className="w-4 h-4 mr-2" /> Calibration XLSX
            </Button>
          )}
          {hasPermission('readiness.recompute') && batchId !== ALL && (
            <Button variant="outline" size="sm" onClick={recompute} disabled={recomputing}
                    data-testid="readiness-recompute-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${recomputing ? 'animate-spin' : ''}`} />
              Recompute now
            </Button>
          )}
          {hasPermission('readiness.configure') && (
            <Button variant="outline" size="sm" onClick={() => setConfigOpen(true)}
                    data-testid="readiness-config-btn">
              <Settings2 className="w-4 h-4 mr-2" /> Profiles
            </Button>
          )}
        </div>
      } />

      {batchId === ALL ? (
        <Card className="p-12 text-center" data-testid="readiness-pick-batch">
          <Gauge className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="font-display text-lg font-semibold">Pick a batch</p>
          <p className="text-sm text-muted-foreground mt-1">
            Readiness is scored per batch — select one in the filter bar above.
          </p>
        </Card>
      ) : loading ? (
        <p className="text-sm text-muted-foreground p-4" data-testid="readiness-loading">Loading readiness…</p>
      ) : !board ? (
        <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="readiness-empty">
          No readiness data for this batch yet — hit “Recompute now” or wait for the nightly run.
        </Card>
      ) : (
        <>
          <Card className="p-4" data-testid="readiness-batch-header">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div>
                <p className="text-xs text-muted-foreground">Batch average</p>
                <p className="font-display text-2xl font-bold" data-testid="readiness-avg-score">
                  {board.stats.avg_score != null ? board.stats.avg_score : '—'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2" data-testid="readiness-state-chips">
                {STATE_ORDER.filter(s => board.stats.state_counts[s]).map(s => (
                  <StateChip key={s} state={s} count={board.stats.state_counts[s]} />
                ))}
              </div>
              <div className="ml-auto text-right">
                <p className="text-xs text-muted-foreground" data-testid="readiness-profile-label">
                  Profile: <span className="font-medium text-foreground">{board.profile.name}</span> v{board.profile.version}
                </p>
                {daysRemaining(board.batch.end_date) != null && (
                  <p className="text-xs text-muted-foreground" data-testid="readiness-days-remaining">
                    {daysRemaining(board.batch.end_date)} days remaining
                  </p>
                )}
              </div>
            </div>
          </Card>

          <ReadinessBoardTable rows={board.trainees} onSelect={setSelected} />
        </>
      )}

      <ReadinessDrawer
        trainee={selected}
        thresholds={board?.profile?.thresholds}
        onClose={() => setSelected(null)}
      />
      <ReadinessProfilesDialog
        open={configOpen}
        onOpenChange={setConfigOpen}
        batchId={batchId === ALL ? null : batchId}
        onChanged={load}
      />
    </div>
  );
}
